import { getMsgPreviewApi, oneKeyReadApi, updateReadStatusApi } from '@/apis/message/message.api';
import {
  MessagePreviewResponse,
  MessageTitleItem,
  MessageTypeEnum,
} from '@/apis/message/message.interface';
import UKPageError from '@/components/UKPageError';
import UKToast from '@/components/UKToast/index';
import ZECustomerService from '@/components/customerService';
import { CustomerFieldsEnum } from '@/constants/customerService';
import { useTranslation } from '@/i18n/client';
import { imageUtil } from '@/utils/imageUtil';
import to from 'await-to-js';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import CustomerServiceIcon from './assets/customer-service.png';
import MessageDetail from './components/messageDetail';
import MessageDialog from './components/messageDialog';
import { TitleProps } from './components/titleItem';
import './index.scss';
import {
  MESSAGE_EVENT_NAMES,
  messageBusinessLogger,
} from '@/utils/logger/instance/messageBusinessLogger';

export interface DetailData {
  showDetail: boolean;
  type: number;
  title: string;
}

interface MessageProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (num: number, show: boolean) => void;
}

export default function Message({ open, onClose, onUpdate }: MessageProps) {
  const { t } = useTranslation();
  const defaultData = { showDetail: false, type: 0, title: '' };
  const [error, setError] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isInitMessage, setIsInitMessage] = useState<boolean>(false);
  const [isInitService, setIsInitService] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [detailData, setDetailData] = useState<DetailData>(defaultData);
  const [totalNum, setTotalNum] = useState<number>(0);
  const [messageNum, setMessageNum] = useState<number>(0);
  const [serviceNum, setServiceNum] = useState<number>(0);
  const [previewData, setPreviewData] = useState<MessagePreviewResponse | null>();
  const [floatTitleList, setFloatTitleList] = useState<TitleProps[]>([]);
  const [fixedTitleList, setFixedTitleList] = useState<TitleProps[]>([]);
  const [serviceFields, setServiceFields] = useState<any>([]);
  const zeRef = useRef<any>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const serviceMsgData: TitleProps = {
    type: MessageTypeEnum.services,
    icon: CustomerServiceIcon.src,
    title: t('messages.customerService.title'),
    content: t('messages.customerService.content'),
    num: 0,
    showDot: false,
    time: '',
    msgId: '',
    orderId: '',
    promoId: '',
  };

  const onPrevDataChange = (previewData: MessagePreviewResponse) => {
    if (previewData) {
      updateMessageNum(previewData);
      if (previewData.topListMessage) updateFixedTitleData(previewData.topListMessage);
      if (previewData.nonTopMessageList) updateFloatTitleData(previewData.nonTopMessageList);
    }
  };

  const init = async () => {
    setIsPageLoading(true);
    const [error, res] = await to(getMsgPreviewApi());
    if (error) {
      setError(true);
      setPreviewData(null);
      // 报错需上报错误日志
      messageBusinessLogger({
        eventName: MESSAGE_EVENT_NAMES.MESSAGE_PAGE_LOAD_FAIL,
        message: {
          isError: true,
        },
      });
    } else if (res && res.code === '200') {
      setError(false);
      onPrevDataChange(res.data);
      setPreviewData(res.data);
    } else {
      setError(false);
      setPreviewData(null);
      setFixedTitleList([serviceMsgData]);
    }
    setIsPageLoading(false);
  };

  const initServiceNum = () => {
    if (window && (window as any).zE) {
      (window as any).zE('messenger:on', 'unreadMessages', function (count: number) {
        setServiceNum(count);
        setIsInitService(true);
        updateServiceTitleData(count);
        if (isInitMessage) {
          setTotalNum(count + messageNum);
        }
      });
    }
  };

  const updateMessageNum = (previewData: MessagePreviewResponse) => {
    let total = 0;
    let showPoint = false;
    if (previewData && previewData.topListMessage) {
      previewData.topListMessage.forEach((item: MessageTitleItem) => {
        if (!item.redPoint) {
          total += item.noticeNum;
        } else if (item.noticeNum > 0) {
          showPoint = true;
        }
      });
    }
    if (previewData && previewData.nonTopMessageList) {
      previewData.nonTopMessageList.forEach((item: MessageTitleItem) => {
        if (!item.redPoint) {
          total += item.noticeNum;
        } else if (item.noticeNum > 0) {
          showPoint = true;
        }
      });
    }
    if (isInitService) {
      const totalCount = total + serviceNum;
      setTotalNum(totalCount);
      onUpdate && onUpdate(totalCount, showPoint);
    }
    setMessageNum(total);
    setIsInitMessage(true);
  };

  const updateFixedTitleData = (fixedList: MessageTitleItem[]) => {
    const topMessageList: TitleProps[] = [serviceMsgData];
    fixedList.forEach((item: MessageTitleItem) => {
      if (item) {
        topMessageList.push({
          type: item.accountType,
          title: item.accountName,
          num: item.noticeNum,
          showDot: item.redPoint,
          icon: imageUtil.concatImgUrlWithoutForceHttps(item.accountIconImage),
          content: item.msgEntityVo ? item.msgEntityVo.title : item.replaceContent,
          time: item.msgEntityVo && item.msgEntityVo.formatTime,
          msgId: item.msgEntityVo && item.msgEntityVo.msgId,
          orderId:
            (item.msgEntityVo &&
              item.msgEntityVo.extraAttribute &&
              String(item.msgEntityVo.extraAttribute.orderId)) ||
            '',
          promoId:
            (item.msgEntityVo &&
              item.msgEntityVo.extraAttribute &&
              String(item.msgEntityVo.extraAttribute.promoId)) ||
            '',
        });
      }
    });
    setFixedTitleList(topMessageList);
  };

  const updateServiceTitleData = (num: number = 0) => {
    const myFixedList = [...fixedTitleList];
    myFixedList.forEach((fixedItem) => {
      if (fixedItem.type == MessageTypeEnum.services) {
        fixedItem.num = num;
      }
    });
    setFixedTitleList(myFixedList);
  };

  const updateFloatTitleData = (floatList: MessageTitleItem[]) => {
    const myFloatList: TitleProps[] = [];
    floatList.forEach((item: MessageTitleItem) => {
      if (item) {
        myFloatList.push({
          type: item.accountType,
          title: item.accountName,
          num: item.noticeNum,
          showDot: item.redPoint,
          icon: imageUtil.concatImgUrlWithoutForceHttps(item.accountIconImage),
          content: item.msgEntityVo && item.msgEntityVo.title,
          time: item.msgEntityVo && item.msgEntityVo.formatTime,
          msgId: item.msgEntityVo && item.msgEntityVo.msgId,
          orderId:
            (item.msgEntityVo &&
              item.msgEntityVo.extraAttribute &&
              String(item.msgEntityVo.extraAttribute.orderId)) ||
            '',
          promoId:
            (item.msgEntityVo &&
              item.msgEntityVo.extraAttribute &&
              String(item.msgEntityVo.extraAttribute.promoId)) ||
            '',
        });
      }
    });
    setFloatTitleList(myFloatList);
  };

  const onOpenDetail = (data: DetailData) => {
    switch (data.type) {
      case MessageTypeEnum.services:
        onCloseDialog();
        if (zeRef.current) {
          zeRef.current.openService();
        }
        break;
      default:
        onReadedStatusChange(data.type);
        setDetailData(data);
    }
  };

  const onBackToHome = (isModify: boolean) => {
    if (isModify) {
      init();
      initServiceNum();
    }
    setDetailData(defaultData);
  };

  const onCloseDialog = () => {
    setIsOpen(false);
    setDetailData(defaultData);
    onClose && onClose();
  };

  // 消息按类型已读
  const onReadedStatusChange = async (type: number) => {
    if (previewData == null) {
      return;
    }
    if (isMessageNoticeNumEmpty(type, previewData.topListMessage)) {
      return;
    }
    if (isMessageNoticeNumEmpty(type, previewData.nonTopMessageList)) {
      return;
    }
    const res = await updateReadStatusApi(type);
    if (res?.data) {
      const myData = { ...previewData };
      clearMessageNoticeNum(type, myData.topListMessage);
      clearMessageNoticeNum(type, myData.nonTopMessageList);
      onPrevDataChange(myData);
      setPreviewData(myData);
    }
  };

  const isMessageNoticeNumEmpty = (type: number, messageList: MessageTitleItem[] | null) => {
    if (messageList) {
      messageList.forEach((item: MessageTitleItem) => {
        if (item.accountType === type && item.noticeNum === 0) {
          return true;
        }
      });
    }
    return false;
  };

  const clearMessageNoticeNum = (type: number, messageList: MessageTitleItem[] | null) => {
    if (messageList) {
      messageList.forEach((item: MessageTitleItem) => {
        if (item.accountType === type) {
          item.noticeNum = 0;
          return;
        }
      });
    }
  };

  const clearAllMessageNoticeNum = (messageList: MessageTitleItem[] | null) => {
    if (messageList) {
      messageList.forEach((item: MessageTitleItem) => {
        item.noticeNum = 0;
      });
    }
  };

  // 一键消息已读
  const oneKeyReaded = async () => {
    if (totalNum <= 0) {
      UKToast.show({
        content: t('common.message.ThereAreCurrentlyNoNewMessages'),
        icon: 'warning',
      });
      return;
    }
    const res = await oneKeyReadApi();
    if (res?.data && previewData) {
      const myData = { ...previewData };
      clearAllMessageNoticeNum(myData.topListMessage);
      clearAllMessageNoticeNum(myData.nonTopMessageList);
      onPrevDataChange(myData);
      setPreviewData(myData);
    }
  };

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    init();
    initServiceNum();
  }, []);

  useEffect(() => {
    if (pathname.includes('/profile/my-orders/order-details')) {
      // 在订单详情页面打开客服对话框需要传订单id
      const orderId = searchParams.get('orderId');
      setServiceFields([{ id: CustomerFieldsEnum.ORDER_NO, value: orderId }]);
    } else if (pathname.includes('/dp')) {
      const href = (window as any).location.href;
      // 在商详页面传商详链接过去
      setServiceFields([{ id: CustomerFieldsEnum.PRODUCT_URL, value: href }]);
    } else {
      setServiceFields([]);
    }
  }, [pathname]);

  if (!isOpen) {
    return null;
  }

  if (error) {
    return (
      <div className='message-frame page-error'>
        <UKPageError
          clickTryAgain={() => {
            init();
            initServiceNum();
          }}
          text={t('common.pageStatus.error')}
          btnText={t('common.btnText.tryAgain')}
        />
      </div>
    );
  }

  return (
    <>
      {detailData && detailData.showDetail ? (
        <MessageDetail
          type={detailData.type}
          title={detailData.title}
          onBack={onBackToHome}
          onClose={onCloseDialog}
        />
      ) : (
        <MessageDialog
          data={{ totalNum, fixedTitleList, floatTitleList, isPageLoading }}
          onOpenDetail={onOpenDetail}
          onClose={onCloseDialog}
          oneKeyRead={oneKeyReaded}
        />
      )}
      <ZECustomerService ref={zeRef} serviceFields={serviceFields} />
    </>
  );
}
